import { ApiEndpoints } from "@shared/api/api-endpoints";
import { onboardingApiClient } from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

// Types for better type safety
export interface AssetClassPayload {
  name: string;
  code: string;
  remote_utility_id: number;
}

export interface AssetTypePayload {
  name: string;
  code: string;
  remote_utility_id: number;
  description?: string;
}

export interface AssetDimensionPayload {
  name: string;
  code: string;
  remote_utility_id: number;
  asset_type_characteristics: "dimensions";
  data_type: string;
  unit: string;
  is_required: string;
}

export interface AssetFailurePayload {
  name: string;
  code: string;
  remote_utility_id: number;
  asset_type_characteristics: "failure_mode";
  description?: string;
}

// Asset Class APIs
export const createAssetClass = async (
  payload: AssetClassPayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const updateAssetClass = async (payload: any): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.patch(url, payload);
  return response.data;
};

export const getAssetClassList = async (
  showInactive: boolean = true
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "asset_class");
      qs.push("show_inactive", showInactive);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

// Asset Type APIs
export const createAssetType = async (
  payload: AssetTypePayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const updateAssetType = async (
  payload: AssetTypePayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.patch(url, payload);
  return response.data;
};

export const getAssetTypeList = async (code: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", "asset_type");
      qs.push("code_list", code);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const getAllAssetTypes = async (): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

// Asset Dimension APIs
export const createAssetDimension = async (
  payload: AssetDimensionPayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const updateAssetDimension = async (
  payload: AssetDimensionPayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.patch(url, payload);
  return response.data;
};

export const getTypeDimensionList = async (
  queryParams?: Record<string, any>
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-mapping",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("map_key", "asset_class_json");
      qs.push("is_pagination_required", true);
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            qs.push(key, value.toString());
          }
        });
      }
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data;
};

// Asset Failure Mode APIs
export const createAssetFailure = async (
  payload: AssetFailurePayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.post(url, payload);
  return response.data;
};

export const getAssetFailureList = async (code: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("code_list", code);
      qs.push("asset_type_characteristics", "failure_mode");
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const updateAssetFailure = async (
  payload: AssetFailurePayload
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json"
  );

  const response = await onboardingApiClient.patch(url, payload);
  return response.data;
};

export const deleteAssetClass = async (code: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
      qs.push("code", code);
    }
  );
  const response = await onboardingApiClient.delete(url);
  return response.data;
};

export const getDimensionList = async (code: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("asset_type_characteristics", "dimensions");
      qs.push("code_list", code);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result;
};

export const searchAssetClassList = async (
  searchData: string
): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "search/onboarding_config",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("search_field", "asset_class_json");
      qs.push("search_data", searchData);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const searchTypeDimension = async (searchData: string): Promise<any> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "search/onboarding_config",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("search_field", "dimension");
      qs.push("search_data", searchData);
    }
  );

  const response = await onboardingApiClient.get(url);
  return response.data?.result;
};

export const deleteDimension = async (code: string): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/asset_class_json",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", getRemoteUtilityId());
      qs.push("code", code);
    }
  );
  const response = await onboardingApiClient.delete(url);
  return response.data;
};

export const toggleConditionParameter = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/condition"
  );

  const response = await onboardingApiClient.patch(url, payload);
  return response.data;
};

